// platform-ui/src/lib/api.ts
// Centralized fetch with session cookie + automatic 401 → login redirect.
// - Lets callers pass plain objects as `body` for JSON.
// - Supports responseType: 'blob' | 'json' | 'text'.
// - Adds X-Project-Id when provided.

import {isProblemDetails} from "./types";
import {enqueueMutation} from "./offlineQueue";

export const API_BASE =
    process.env.NEXT_PUBLIC_API_BASE ?? "http://localhost:8080";

const OFFLINE_QUEUEABLE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/**
 * iter-884: when fetch throws and the browser is offline, enqueue mutation
 * requests for replay on reconnect. Without this, the OfflineIndicator's
 * "changes will sync when you reconnect" message rendered globally in
 * app/layout.tsx is misleading — the queue infrastructure existed but
 * nothing populated it.
 *
 * Skips Blob/FormData/ReadableStream bodies because IndexedDB cannot
 * round-trip them safely (file uploads should not be replayed).
 */
async function maybeEnqueueOnOffline(url: string, init: RequestInit): Promise<void> {
    if (typeof navigator === "undefined" || navigator.onLine !== false) return;
    const method = (init.method ?? "GET").toUpperCase();
    if (!OFFLINE_QUEUEABLE_METHODS.has(method)) return;
    const body = init.body;
    if (body != null && typeof body !== "string") return;
    const headers: Record<string, string> = {};
    if (init.headers) {
        const h = init.headers instanceof Headers ? init.headers : new Headers(init.headers);
        h.forEach((v, k) => { headers[k] = v; });
    }
    try {
        await enqueueMutation({ url, method, headers, body: body ?? null });
    } catch {
        // Queue failure (e.g. IndexedDB blocked, no storage) must not crash
        // the api wrapper. Caller still sees the network error.
    }
}

/**
 * Detect if the current browser context is a project domain (not the admin platform).
 * On project domains, API calls use /api/{endpoint} instead of /api/projects/{id}/{endpoint}.
 *
 * <p>iter-1247: fail-closed when {@code NEXT_PUBLIC_PLATFORM_DOMAIN} is
 * unset. Pre-1247 the check was:
 * <pre>host !== platformDomain && host !== 'localhost' && host !== '127.0.0.1'</pre>
 * With an empty {@code platformDomain}, every non-localhost host
 * compared as not-equal — so EVERY admin-domain request was
 * misidentified as a project domain. The frontend then shortened
 * {@code /api/projects/{id}/recent-records} → {@code /api/recent-records},
 * the backend's {@code ApiPathRewriteFilter} found no
 * {@code ProjectContextHolder} entry (admin domain has no domain→project
 * mapping in {@code DomainRoute}), and the request fell through to a
 * 404 static-resource handler. Result: every admin page that used
 * {@code api()} 404s on initial load.
 *
 * <p>Now: if the platform domain isn't configured, assume admin domain
 * and never shorten paths. Worst case is benign — project-domain
 * requests use the long {@code /api/projects/{id}/...} path, which
 * the backend still accepts (the rewrite filter only handles the
 * short form). Best case the admin pages stop 404'ing.
 */
function isProjectDomain(): boolean {
    if (typeof window === 'undefined') return false;
    const host = window.location.hostname;
    const platformDomain = process.env.NEXT_PUBLIC_PLATFORM_DOMAIN ?? '';
    if (!platformDomain) return false; // iter-1247: fail-closed on missing config
    return !!host && host !== platformDomain && host !== 'localhost' && host !== '127.0.0.1';
}

/**
 * On project domains, shorten /api/projects/{uuid}/... to /api/...
 * The backend ApiPathRewriteFilter reconstructs the full path from domain context.
 */
function shortenProjectApiPath(path: string): string {
    if (!isProjectDomain()) return path;
    const match = path.match(/^\/api\/projects\/[0-9a-f-]{36}\/(.*)$/);
    return match ? `/api/${match[1]}` : path;
}

type ResponseType = "blob" | "json" | "text";

export type ApiInit = Omit<RequestInit, "body" | "headers"> & {
    /** You can pass Headers or a record of header values */
    headers?: HeadersInit;
    /** Plain object (auto-JSON), string, Blob, FormData, etc. */
    body?: BodyInit | Record<string, any> | null;
    /** Optional project scoping header */
    projectId?: string;
    /** Optional preview role for testing with specific role permissions */
    previewRole?: string | null;
    /** Optional preview user id for impersonation in preview mode */
    previewUserId?: string | null;
    /** Force response type instead of inferring from Content-Type */
    responseType?: ResponseType;
    /**
     * Opt out of the global 401 → login-redirect behavior. Useful for
     * data-fetch endpoints (file lookups, optional widget data) where a
     * 401 is a "permission denied for this specific resource" signal
     * rather than "session expired" — the caller should handle the
     * error inline (empty state, toast) without throwing the user out
     * of the app.
     *
     * iter-1114: user-reported regression — a single 401 on
     * /api/_files/by-records was logging the entire app out. Pre-1114
     * every api() caller triggered redirectToLoginPage on 401. Now
     * non-auth-critical fetches can set this flag and the caller gets
     * the ApiError without the redirect side-effect.
     */
    skipAuthRedirect?: boolean;
};

/**
 * iter-1539: callback hook for 403 responses. Allows {@code queryClient.ts}
 * to install a "refetch /auth/me" handler without {@code api.ts} taking
 * a hard dependency on the React Query singleton (which would create a
 * circular import: queryClient.ts → api.ts → queryClient.ts).
 *
 * <p>The threat model: a project user with devtools access could tamper
 * with the persisted {@code anc-app-auth} sessionStorage entry to spoof
 * a higher role and render forbidden UI affordances. The server-side
 * {@code PolicyEnforcer} re-evaluates roles from the DB on every API
 * call, so any actual mutation/read still hits a 403. We use that 403
 * as a canary: invalidate the React Query {@code ['me']} cache so the
 * next consumer read refetches roles from {@code /auth/me} (server-
 * trusted) and the tampered in-memory state self-corrects.
 *
 * <p>iter-1547: the handler signature widens to receive the {@code url}
 * of the 403'd request. {@code queryClient.ts} inspects the URL for the
 * {@code /api/projects/<uuid>/...} pattern so it can ALSO refresh the
 * imperative {@code appGetMe(projectId)} cache that drives the app-user
 * role surface (sidebar chips, role-gated UI). Without this, the
 * iter-1539 canary covers only the admin OAuth {@code ['me']} key — a
 * project user's tampered role array would survive the 403 in-memory.
 *
 * <p>Default is {@code null} (no-op). The actual handler is installed
 * by {@code queryClient.ts} at module-load time.
 */
type ForbiddenHandler = (url: string) => void;
let forbiddenHandler: ForbiddenHandler | null = null;

/**
 * Install a callback invoked once per 403 response. Passing {@code null}
 * removes the handler. Used by {@code queryClient.ts} to wire the
 * {@code ['me']} cache invalidation; also useful in tests.
 *
 * <p>iter-1547: the handler now receives the 403'd request URL so the
 * queryClient installer can branch on {@code /api/projects/<uuid>/...}
 * paths and also refresh the imperative app-user identity cache.
 */
export function setForbiddenHandler(handler: ForbiddenHandler | null): void {
  forbiddenHandler = handler;
}

export class ApiError<TBody = any> extends Error {
    readonly status: number;
    readonly body: TBody | string | null;
    readonly headers: Headers;
    readonly url: string;

    constructor(
        status: number,
        statusText: string,
        body: TBody | string | null,
        headers: Headers,
        url: string
    ) {
        // Build a descriptive error message including violations if present
        let message: string;
        if (isProblemDetails(body)) {
            const parts: string[] = [];
            if (body.detail) parts.push(body.detail);
            else if (body.title) parts.push(body.title);
            // Include violations array if present (e.g., from PCF validation)
            const violations = body.violations;
            if (Array.isArray(violations) && violations.length > 0) {
                parts.push(violations.join('\n'));
            }
            message = parts.join(': ') || `HTTP ${status}`;
        } else if (typeof body === "string" && body) {
            message = body;
        } else {
            message = statusText || `HTTP ${status}`;
        }
        super(message);
        this.name = "ApiError";
        this.status = status;
        this.body = body;
        this.headers = headers;
        this.url = url;
    }
}

function isBodyInit(x: unknown): x is BodyInit {
    // Heuristic: treat string/Blob/FormData/ArrayBuffer/DataView/TypedArray/URLSearchParams/ReadableStream as BodyInit
    return (
        typeof x === "string" ||
        x instanceof Blob ||
        x instanceof FormData ||
        x instanceof URLSearchParams ||
        x instanceof ReadableStream ||
        ArrayBuffer.isView(x) ||
        x instanceof ArrayBuffer
    );
}

/** Normalize init into a real RequestInit (stringify objects, set headers, etc.) */
function redirectToLoginPage() {
    if (typeof window === "undefined") return;

    const pathname = window.location.pathname;

    // Check if the current path is a preview or app route:
    // /preview/{projectId}/... or /app/{projectId}/... (with optional locale prefix)
    const appRouteMatch = pathname.match(/^(?:\/([a-z]{2}))?\/(preview|app)\/([^/]+)/);
    if (appRouteMatch) {
        const locale = appRouteMatch[1]; // e.g. "sv" or undefined
        const routePrefix = appRouteMatch[2]; // "preview" or "app"
        const projectId = appRouteMatch[3];
        // Do not redirect if already on auth pages — prevents redirect loops
        const authPattern = new RegExp(`^(?:\\/[a-z]{2})?\\/${routePrefix}\\/${projectId}\\/auth\\/`);
        if (authPattern.test(pathname)) return;

        // Redirect to the app user login page with the current path as the continue URL
        const continueUrl = encodeURIComponent(pathname + (window.location.search || ""));
        const localePrefix = locale ? `/${locale}` : '';
        window.location.href = `${localePrefix}/${routePrefix}/${projectId}/auth/login?continue=${continueUrl}`;
        return;
    }

    const current = window.location.href;
    const params = new URLSearchParams();
    if (current) {
        params.set("continue", current);
    }
    // Extract locale from current path for admin login redirect
    const adminLocaleMatch = pathname.match(/^\/([a-z]{2})\//);
    const adminLocale = adminLocaleMatch ? adminLocaleMatch[1] : null;
    const loginPath = adminLocale ? `/${adminLocale}/login` : '/login';
    const url = params.toString() ? `${loginPath}?${params.toString()}` : loginPath;
    // Check for login page with or without locale prefix (e.g., /login or /sv/login)
    if (pathname === "/login" || /^\/[a-z]{2}\/login$/.test(pathname)) return;
    window.location.href = url;
}

function buildFetchInit(init?: ApiInit): RequestInit {
    const headers = new Headers(init?.headers ?? {});
    const hasContentType = !!headers.get("Content-Type");

    // Optional project scoping
    if (init?.projectId) {
        headers.set("X-Project-Id", init.projectId);
    }

    // Optional preview role for testing permissions
    if (init?.previewRole) {
        headers.set("X-Preview-Role", init.previewRole);
    }

    // Optional preview user id for impersonation
    if (init?.previewUserId) {
        headers.set("X-Preview-User", init.previewUserId);
    }

    // In preview routes (NOT app routes), fall back to persisted preview session
    // values when explicit headers are not provided by the caller.
    if (typeof window !== "undefined") {
        const pathname = window.location.pathname;
        const isPreviewRoute =
            pathname.startsWith("/preview/") || /^\/[a-z]{2}\/preview\//.test(pathname);
        if (isPreviewRoute) {
            try {
                const raw = window.sessionStorage.getItem("anc-preview-session");
                if (raw) {
                    const persisted = JSON.parse(raw);
                    const state = persisted?.state ?? {};
                    const storedRole = typeof state.previewRole === "string" ? state.previewRole : null;
                    const storedUser = typeof state.previewUserId === "string" ? state.previewUserId : null;
                    if (!headers.get("X-Preview-Role") && storedRole) {
                        headers.set("X-Preview-Role", storedRole);
                    }
                    if (!headers.get("X-Preview-User") && storedUser) {
                        headers.set("X-Preview-User", storedUser);
                    }
                }
            } catch {
                // Ignore sessionStorage parse/access errors
            }
        }
    }

    // Normalize body
    let body: BodyInit | undefined;
    if (init && "body" in init) {
        const b = init.body;
        if (b == null) {
            body = undefined; // no body
        } else if (isBodyInit(b)) {
            body = b; // Blob/FormData/string/etc.
            // A plain STRING body is JSON everywhere in this codebase (callers pass
            // JSON.stringify(...)). Default its Content-Type to application/json when
            // unset, so it is NOT sent as text/plain — which a Spring @RequestBody
            // JSON endpoint rejects with HttpMediaTypeNotSupportedException (500).
            // Blob/FormData keep the browser-computed type; an explicit header
            // (e.g. the CSV-import text/plain callers) always wins.
            if (typeof b === "string" && b && !hasContentType) {
                headers.set("Content-Type", "application/json");
            }
        } else {
            body = JSON.stringify(b); // plain object → JSON
            if (!hasContentType) headers.set("Content-Type", "application/json");
        }
    }

    // Default Accept
    if (!headers.get("Accept")) {
        headers.set("Accept", "application/json, text/plain, */*");
    }

    // Build a clean RequestInit; only include defined fields (helps exactOptionalPropertyTypes)
    return {
        headers,
        ...(init?.method !== undefined ? {method: init.method} : {}),
        ...(init?.credentials !== undefined ? {credentials: init.credentials} : {credentials: "include"}),
        ...(body !== undefined ? {body} : {}), // <- use normalized `body` here
        ...(init?.cache !== undefined ? {cache: init.cache} : {}),
        ...(init?.integrity !== undefined ? {integrity: init.integrity} : {}),
        ...(init?.keepalive !== undefined ? {keepalive: init.keepalive} : {}),
        ...(init?.mode !== undefined ? {mode: init.mode} : {}),
        ...(init?.redirect !== undefined ? {redirect: init.redirect} : {}),
        ...(init?.referrer !== undefined ? {referrer: init.referrer} : {}),
        ...(init?.referrerPolicy !== undefined ? {referrerPolicy: init.referrerPolicy} : {}),
        ...(init?.signal !== undefined ? {signal: init.signal} : {}),
        // Do NOT spread non-standard fields like `window` or `priority` onto RequestInit
    };
}


async function readBody(
    res: Response,
    forced?: ResponseType
): Promise<any | string | Blob | null> {
    // Handle 204/205 or empty body
    const contentLength = res.headers.get("content-length");
    if (res.status === 204 || res.status === 205 || contentLength === "0") return null;

    if (forced === "blob") return await res.blob();
    if (forced === "text") return await res.text();
    if (forced === "json") {
        // try JSON, fall back to text
        const t = await res.text();
        if (!t) return null;
        try {
            return JSON.parse(t);
        } catch {
            return t;
        }
    }

    // Infer from content-type
    const ct = res.headers.get("content-type") || "";
    if (ct.includes("application/json") || ct.includes("application/problem+json")) {
        const t = await res.text();
        if (!t) return null;
        try {
            return JSON.parse(t);
        } catch {
            return t;
        }
    }
    if (ct.startsWith("text/")) return await res.text();
    return await res.blob();
}

/**
 * Fetch wrapper returning JSON/Blob/Text per responseType or content-type.
 * Throws ApiError on non-2xx with { status, body, headers, url }.
 */
export async function api<T = any>(path: string, init?: ApiInit): Promise<T> {
    const fetchInit = buildFetchInit(init);
    const url = `${API_BASE}${shortenProjectApiPath(path)}`;

    const res = await fetch(url, fetchInit).catch(async (e) => {
        // Network/DNS/timeout-level failure. iter-884: persist mutating
        // requests to the offline queue when the browser is offline so the
        // OfflineIndicator's auto-replay-on-reconnect handler can flush them.
        await maybeEnqueueOnOffline(url, fetchInit);
        throw new ApiError(0, "Network error", e?.message ?? null, new Headers(), url);
    });

    if (res.status === 401) {
        const body = await readBody(res, init?.responseType);
        // iter-1114: allow data-fetch callers to opt out of the global
        // login-redirect. Without this flag a 401 on any single endpoint
        // kicks the user out of the app entirely — surprising when the
        // failure is permission-scope on one resource, not session expiry.
        if (!init?.skipAuthRedirect) {
            try {
                redirectToLoginPage();
            } catch {
                /* ignore */
            }
        }
        throw new ApiError(res.status, res.statusText, body, res.headers, res.url);
    }

    if (res.status === 403) {
        // iter-1539: tamper / stale-role canary. The caller still gets the
        // ApiError (403 is "no permission for THIS action" — surface it as
        // a normal error, do NOT redirect to login). The registered handler
        // invalidates the React Query ['me'] cache so the next read of the
        // identity refetches from /auth/me (server-trusted). Roles in the
        // store will re-render with the server's view within one tick.
        //
        // iter-1547: pass the original request `path` (not res.url, which
        // includes the API_BASE host) so the handler can branch on the
        // /api/projects/<uuid>/... pattern and also refresh the imperative
        // appGetMe(projectId) cache. The path is host-relative and stable
        // across the API_BASE-prefixing logic above.
        const body = await readBody(res, init?.responseType);
        if (forbiddenHandler) {
            try {
                forbiddenHandler(path);
            } catch {
                // A handler failure must never mask the underlying 403 —
                // the caller still needs to see and handle the error.
            }
        }
        throw new ApiError(res.status, res.statusText, body, res.headers, res.url);
    }

    const body = await readBody(res, init?.responseType);

    if (!res.ok) {
        throw new ApiError(res.status, res.statusText, body, res.headers, res.url);
    }

    return body as T;
}

export interface CascadePreviewResult {
  countsByModel: Record<string, number>;
  totalAffected: number;
}

export async function previewCascade(
  projectId: string,
  model: string,
  recordId: string
): Promise<CascadePreviewResult> {
  return api<CascadePreviewResult>(
    `/api/projects/${projectId}/${model}/${recordId}/cascade-preview`,
    { method: 'GET', projectId }
  );
}

export interface TransitionHistoryEntry {
  fromState: string;
  toState: string;
  label: string | null;
  transitionedBy: string | null;
  transitionedAt: string;
}

export async function getWorkflowHistory(
  projectId: string,
  model: string,
  recordId: string,
  fieldName?: string
): Promise<{ history: TransitionHistoryEntry[] }> {
  const params = fieldName ? `?field=${fieldName}` : '';
  return api(`/api/projects/${projectId}/${model}/${recordId}/workflow-history${params}`, {
    projectId,
  });
}

export interface BulkOperationResult {
  id: string;
  status: number;
  message: string | null;
}

export interface BulkResponse {
  results: BulkOperationResult[];
  total: number;
}

export async function bulkDelete(
  projectId: string,
  model: string,
  ids: string[]
): Promise<BulkResponse> {
  return api<BulkResponse>(
    `/api/projects/${projectId}/${model}/bulk-delete`,
    {
      method: 'POST',
      body: { ids },
      projectId,
    }
  );
}

export async function bulkUpdate(
  projectId: string,
  model: string,
  ids: string[],
  patch: Record<string, unknown>
): Promise<BulkResponse> {
  return api<BulkResponse>(
    `/api/projects/${projectId}/${model}/bulk-update`,
    {
      method: 'POST',
      body: { ids, patch },
      projectId,
    }
  );
}

/**
 * Response shape for {@link bulkUpdateByFilter} (NEW-P7).
 *
 * - `matched`: total records that matched the filter (pre-patch)
 * - `updated`: number of records successfully patched
 * - `errors`:  per-record failures (status >= 400) with id + message
 */
export interface BulkUpdateByFilterResponse {
  matched: number;
  updated: number;
  errors: BulkOperationResult[];
}

/**
 * Body shape for {@link bulkUpdateByFilter}.
 *
 * - `filter` accepts flat `{field: value}` pairs for exact-match filtering plus an
 *   optional reserved `expr` key for a CEL predicate (same contract as the list
 *   endpoint's `filter[…]` params).
 * - `ids` (alternative to `filter`) lets UIs with explicit row-selection target
 *   specific records. When provided, the backend routes through the existing
 *   per-record patch path. When both are present the `ids` path wins.
 * - `updates` is the patch to apply to each matching record (must be non-empty).
 * - `limit` clamps how many matching records may be patched in one call
 *   (default 1000, max 10000).
 */
export interface BulkUpdateByFilterBody {
  filter?: Record<string, unknown>;
  ids?: string[];
  updates: Record<string, unknown>;
  limit?: number;
}

/**
 * Filter-based atomic bulk update (NEW-P7).
 *
 * Finds all records matching {@code filter} (respecting row-level policies) and
 * applies {@code updates} to each within a single database transaction. Returns
 * `matched`, `updated`, and per-record `errors`. Example:
 * "mark every invoice past due as overdue" in one call.
 */
export async function bulkUpdateByFilter(
  projectId: string,
  model: string,
  body: BulkUpdateByFilterBody,
): Promise<BulkUpdateByFilterResponse> {
  return api<BulkUpdateByFilterResponse>(
    `/api/projects/${projectId}/${model}/_bulk-update`,
    {
      method: 'POST',
      body,
      projectId,
    },
  );
}

export interface ImportReportIssue {
  model: string;
  line: number;
  code: string;
  field: string | null;
  message: string;
  value: string | null;
  /**
   * Raw CSV row as a field -> raw string map. Null for pre-row structural errors
   * (bad_block, unknown_model, bad_header).
   */
  row?: Record<string, string> | null;
}

export interface ImportReport {
  totalBlocks: number;
  totalRows: number;
  created: number;
  updated: number;
  skipped: number;
  issues: ImportReportIssue[];
  /**
   * Row-level errors (subset of {@link issues} scoped to row insert/update failures),
   * capped at 100 entries. Populated by backend starting NEW-P4.
   */
  errors?: ImportReportIssue[];
  /** Total row-level errors encountered (may exceed {@link errors} length when capped). */
  totalFailed?: number;
  /** True when {@link totalFailed} exceeded the cap and {@link errors} was truncated. */
  truncated?: boolean;
}

export async function importCsvDryRun(
  projectId: string,
  model: string,
  csvText: string
): Promise<ImportReport> {
  return api<ImportReport>(
    `/api/projects/${projectId}/${model}/_import?format=csv&dryRun=true`,
    {
      method: 'POST',
      body: csvText,
      headers: { 'Content-Type': 'text/plain' },
      projectId,
    }
  );
}

export async function importCsv(
  projectId: string,
  model: string,
  csvText: string
): Promise<ImportReport> {
  return api<ImportReport>(
    `/api/projects/${projectId}/${model}/_import?format=csv`,
    {
      method: 'POST',
      body: csvText,
      headers: { 'Content-Type': 'text/plain' },
      projectId,
    }
  );
}

// --- Background Jobs ---

export interface BackgroundJobDto {
    id: string;
    type: string;
    status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
    progress: number;
    progressMessage: string | null;
    resultType: 'download' | 'summary' | null;
    resultUrl: string | null;
    resultSummary: string | null;
    errorMessage: string | null;
    attempts: number;
    maxAttempts: number;
    createdBy: string | null;
    createdAt: string;
    startedAt: string | null;
    completedAt: string | null;
    cancelledAt: string | null;
}

export async function listBackgroundJobs(projectId: string): Promise<BackgroundJobDto[]> {
    return api<BackgroundJobDto[]>(`/api/projects/${projectId}/jobs`, { projectId });
}

export async function getBackgroundJob(projectId: string, jobId: string): Promise<BackgroundJobDto> {
    return api<BackgroundJobDto>(`/api/projects/${projectId}/jobs/${jobId}`, { projectId });
}

export async function submitBackgroundJob(
    projectId: string, type: string, params?: Record<string, unknown>
): Promise<BackgroundJobDto> {
    return api<BackgroundJobDto>(`/api/projects/${projectId}/jobs`, {
        method: 'POST',
        body: { type, params: params ? JSON.stringify(params) : null },
        projectId,
    });
}

export async function cancelBackgroundJob(
    projectId: string, jobId: string
): Promise<{ cancelled: boolean }> {
    return api<{ cancelled: boolean }>(`/api/projects/${projectId}/jobs/${jobId}/cancel`, {
        method: 'POST',
        projectId,
    });
}

export async function retryBackgroundJob(
    projectId: string, jobId: string
): Promise<BackgroundJobDto> {
    return api<BackgroundJobDto>(`/api/projects/${projectId}/jobs/${jobId}/retry`, {
        method: 'POST',
        projectId,
    });
}

// --- Advanced Query ---

export interface QueryBody {
    filter?: object;
    sort?: string[];
    page?: number;
    size?: number;
    includeInactive?: boolean;
}

export async function queryRecords<T>(
    projectId: string,
    model: string,
    body: QueryBody
): Promise<T> {
    return api<T>(`/api/projects/${projectId}/${model}/_query`, {
        method: 'POST',
        body,
        projectId,
    });
}

// --- Bulk Data Plan (iter-1554) ---

/**
 * One dataPlan entry — what a single widget needs to fetch. The
 * server treats every plan independently: per-plan policy check,
 * per-plan row guard, per-plan result wrapper. A plan whose
 * `model` is missing or whose auth fails does NOT fail the batch.
 *
 * See {@link BulkDataPlanResult} for the per-plan response shape.
 */
export interface DataPlanRequest {
    /**
     * Opaque correlation id the client uses to match the response slice
     * back to the requesting widget. Pass-through; the server never
     * inspects it.
     */
    widget: string;
    model: string;
    filter?: Record<string, unknown>;
    sort?: Array<{ field: string; dir?: 'asc' | 'desc' }>;
    pagination?: { page?: number; size?: number };
}

/**
 * Per-plan response slice. `status` ∈ {"ok", "forbidden", "error"}.
 * - `"ok"`: `data` is the raw `_query` response (items / page / size / hasMore).
 * - `"forbidden"`: caller lacks `read` policy on the model. `data` is undefined.
 * - `"error"`: per-plan failure. `error` carries a short truncated message.
 */
export interface BulkDataPlanResult<T = unknown> {
    index: number;
    widget: string;
    status: 'ok' | 'forbidden' | 'error';
    data?: T;
    error?: string;
}

export interface BulkDataPlanResponse<T = unknown> {
    results: Array<BulkDataPlanResult<T>>;
}

/**
 * Server cap on per-request plan count (mirrors
 * {@code BulkDataPlanController.MAX_PLANS}). Frontend callers may pre-chunk
 * larger plan arrays into multiple requests.
 */
export const MAX_BULK_DATA_PLANS = 50;

/**
 * Coalesce N dataPlan reads into ONE server round-trip. The endpoint
 * returns 200 regardless of per-plan failures — inspect each
 * {@link BulkDataPlanResult#status} to decide rendering. A 400 only
 * occurs when the caller exceeds {@link MAX_BULK_DATA_PLANS}.
 */
export async function bulkQueryRecords<T = unknown>(
    projectId: string,
    plans: DataPlanRequest[],
): Promise<BulkDataPlanResponse<T>> {
    return api<BulkDataPlanResponse<T>>(
        `/api/projects/${projectId}/_bulk-data`,
        {
            method: 'POST',
            body: { plans },
            projectId,
        },
    );
}

/**
 * Request body for {@link getExportPreview}. Mirrors the server-side
 * {@code _export-preview} endpoint; all fields are optional.
 */
export interface ExportPreviewBody {
    filters?: Record<string, string>;
    sort?: string[];
    includeInactive?: boolean;
    q?: string;
    columns?: string[];
    displayNames?: boolean;
}

export interface ExportPreviewMetadata {
    model: string;
    exportedAt: string;
    filters: Record<string, string>;
    sort: string[];
    includeInactive: boolean;
    q?: string;
    columns?: string[];
    displayNames: boolean;
}

export interface ExportPreviewResponse {
    metadata: ExportPreviewMetadata;
    estimatedCount: number;
    previewRowLimit: number;
    rows: Array<Record<string, unknown>>;
}

/**
 * Fetch an export preview for the given model. Returns metadata describing
 * the filters/sort that will be applied, an estimated total row count, and
 * the first N rows (bounded by the server-configured limit) so the user
 * can confirm what the export will contain before downloading.
 */
export async function getExportPreview(
    projectId: string,
    model: string,
    body: ExportPreviewBody
): Promise<ExportPreviewResponse> {
    return api<ExportPreviewResponse>(
        `/api/projects/${projectId}/${model}/_export-preview`,
        {
            method: 'POST',
            body,
            projectId,
        }
    );
}
